'use client'

/**
 * Live workspace telemetry: active-user count + host resource usage.
 *
 * Polls /api/system/status on a SHARED, module-scoped interval — the
 * snapshot, the in-flight flag and the timer live outside React, so
 * navigating between pages doesn't tear the badge down to a blank
 * state. Re-mounted Navigation instances pick up the cached snapshot
 * instantly and the interval keeps ticking only while at least one
 * authenticated consumer is mounted.
 *
 * Slots rendered (always, never reflowing):
 *   - active users  (mandatory)
 *   - CPU%          (md+)
 *   - RAM%          (md+)
 *   - GPU%          (lg+, falls back to "—" when no GPU is detected)
 *
 * Failures are silent: a network blip keeps the last good snapshot
 * on screen rather than flashing an error in the nav.
 */

import { useEffect, useState } from 'react'
import {
  Users,
  Cpu,
  MemoryStick,
  Gauge,
} from 'lucide-react'

interface HostBlock {
  available?: boolean
  cpu_percent?: number
  cpu_count?: number
  mem_percent?: number
  mem_used_gb?: number
  mem_total_gb?: number
  load_avg_1m?: number | null
}

interface GpuDevice {
  name: string
  util_percent: number
  mem_used_mb: number
  mem_total_mb: number
  mem_percent: number
  temp_c: number
}

interface GpuBlock {
  available?: boolean
  count?: number
  devices?: GpuDevice[]
}

export interface SystemStatusPayload {
  active_users?: number
  active_sessions?: number
  host?: HostBlock
  gpu?: GpuBlock
  cached?: boolean
}

const POLL_MS = 15_000 // backend caches the heavy bits for 4s; 15s outer cadence
const STALE_MS = 8_000 // refetch on (re)subscribe if older than this

// ────────────────────── module-scoped singleton store ──────────────────────
type Listener = (s: SystemStatusPayload | null) => void

let lastSnapshot: SystemStatusPayload | null = null
let lastFetchedAt = 0
let inFlight = false
let pollTimer: ReturnType<typeof setInterval> | null = null
const listeners = new Set<Listener>()
const emit = () => listeners.forEach((l) => l(lastSnapshot))

async function pollOnce() {
  if (inFlight) return
  inFlight = true
  try {
    const res = await fetch('/api/system/status', {
      credentials: 'same-origin',
      cache: 'no-store',
    })
    if (!res.ok) return
    lastSnapshot = (await res.json()) as SystemStatusPayload
    lastFetchedAt = Date.now()
    emit()
  } catch {
    // network blip — keep the last good snapshot
  } finally {
    inFlight = false
  }
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  // Hand out the cached value immediately so the consumer doesn't see
  // a null on mount when we already have data from a previous page.
  if (lastSnapshot != null) listener(lastSnapshot)
  // First listener → start the shared poll loop.
  if (pollTimer == null) {
    pollTimer = setInterval(pollOnce, POLL_MS)
  }
  // Stale → refetch now.
  if (Date.now() - lastFetchedAt > STALE_MS) {
    void pollOnce()
  }
  return () => {
    listeners.delete(listener)
    // Last listener gone → stop the timer (cached snapshot is preserved).
    if (listeners.size === 0 && pollTimer != null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }
}

// ──────────────────────────── presentation helpers ──────────────────────────
function fmtPct(v: number | undefined | null): string {
  if (v == null || Number.isNaN(v)) return '—'
  return `${Math.round(v)}%`
}

function loadColor(pct: number | undefined | null): string {
  if (pct == null) return 'text-slate-400'
  if (pct >= 85) return 'text-danger-600'
  if (pct >= 65) return 'text-amber-600'
  return 'text-emerald-600'
}

// ────────────────────────────── component ──────────────────────────────────
export function SystemStatus({ enabled = true }: { enabled?: boolean }) {
  // Seed from the module cache so re-mounts (page navigation) never
  // flash an empty badge — first paint already shows the last known
  // values; the poll loop just refreshes them in place.
  const [data, setData] = useState<SystemStatusPayload | null>(
    () => lastSnapshot,
  )

  useEffect(() => {
    if (!enabled) return
    return subscribe(setData)
  }, [enabled])

  if (!enabled) return null

  const users = data?.active_users
  const cpu = data?.host?.cpu_percent
  const mem = data?.host?.mem_percent
  const memUsed = data?.host?.mem_used_gb
  const memTot = data?.host?.mem_total_gb
  const gpu = data?.gpu?.available ? data.gpu.devices?.[0] : null
  const gpuPct = gpu?.util_percent
  const cpuCount = data?.host?.cpu_count

  const title = [
    users != null ? `${users} user${users === 1 ? '' : 's'} active` : null,
    cpu != null ? `CPU ${fmtPct(cpu)}${cpuCount ? ` of ${cpuCount}c` : ''}` : null,
    mem != null
      ? `RAM ${fmtPct(mem)} (${memUsed?.toFixed(1)}/${memTot?.toFixed(1)} GB)`
      : null,
    gpu
      ? `GPU ${gpu.name} ${fmtPct(gpu.util_percent)} · ${gpu.temp_c}°C · ${
          gpu.mem_used_mb / 1024
        }GB/${gpu.mem_total_mb / 1024}GB`
      : 'GPU not detected on host',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div
      title={title}
      aria-label={title}
      className="flex items-center gap-3 rounded-md border border-slate-200/80 bg-slate-50/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 backdrop-blur-sm"
    >
      {/* active users — always visible, with the live-dot */}
      <span className="flex items-center gap-1.5">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        <Users className="h-3 w-3 text-slate-500" />
        <span className="tabular-nums text-slate-700">
          {users != null ? users : '—'}
        </span>
      </span>

      {/* CPU — md and up */}
      <span className="hidden items-center gap-1 md:inline-flex">
        <Cpu className="h-3 w-3 text-slate-500" />
        <span className={`tabular-nums ${loadColor(cpu)}`}>{fmtPct(cpu)}</span>
      </span>

      {/* RAM — md and up */}
      <span className="hidden items-center gap-1 md:inline-flex">
        <MemoryStick className="h-3 w-3 text-slate-500" />
        <span className={`tabular-nums ${loadColor(mem)}`}>{fmtPct(mem)}</span>
      </span>

      {/* GPU — always rendered; slot reserved with "—" when no GPU. */}
      <span className="hidden items-center gap-1 lg:inline-flex">
        <Gauge className="h-3 w-3 text-slate-500" />
        <span className={`tabular-nums ${loadColor(gpuPct)}`}>
          {gpu ? fmtPct(gpuPct) : '—'}
        </span>
      </span>
    </div>
  )
}
